import { Service, inject } from '@piwikpro/platform';
import { createAction } from 'redux-actions';
import merge from 'lodash/merge';
import { HttpClient } from '@piwikpro/http-crate';
import { ProductAnalytics } from '@piwikpro/product-analytics/ProductAnalytics';
import {
  NotifyOnFail,
  NotifyAboutCreation,
  NotifyAboutDelete,
  NotifyAboutUpdate,
} from '@piwikpro/notification-crate';
import type { Store } from 'redux';
import { Confirmation } from '@piwikpro/confirmation-crate';
import {
  AppId,
  AppAttributes,
  SortOption,
  SortDirection,
  CreateResponse,
  App,
  DetailsResponse,
  CollectionResponse,
} from './model';

export interface FindOptions {
  search?: string
  offset?: number
  sort?: SortOption
  limit?: number
}

export const appsFetchInitialized = createAction<FindOptions, FindOptions>(
  '@platform/query/apps/list/init',
  payload => payload,
);
export const appsFetchSucceeded = createAction<CollectionResponse<App>, CollectionResponse<App>>(
  '@platform/query/apps/list/success',
  payload => payload,
);
export const appsFetchFailed = createAction(
  '@platform/query/apps/list/failed',
);
export const appCreationInitialized = createAction(
  '@platform/command/apps/create/init',
);
export const appCreationSucceeded = createAction(
  '@platform/command/apps/create/success',
);
export const appCreationFailed = createAction(
  '@platform/command/apps/create/failed',
);
export const appDeleteSucceeded = createAction(
  '@platform/command/apps/delete/success',
);
export const appSettingsFetchInitialized = createAction(
  '@platform/query/apps/get/init',
);
export const appSettingsFetchSucceeded = createAction<App, App>(
  '@platform/query/apps/get/success',
  payload => payload,
);
export const appSettingsFetchFailed = createAction(
  '@platform/query/apps/get/failed',
);
export const appSettingsUpdateInitialized = createAction<
Partial<AppAttributes>, Partial<AppAttributes>
>(
  '@platform/command/apps/update/init',
  payload => payload,
);
export const appSettingsUpdateSucceeded = createAction<Partial<
AppAttributes>, Partial<AppAttributes>
>(
  '@platform/command/apps/update/success',
  payload => payload,
);
export const appSettingsUpdateFailed = createAction(
  '@platform/command/apps/update/failed',
);
export const appsCnilSettingFetchInitialized = createAction(
  '@platform/query/apps/get/singleParam/cnil/init',
);
export const appsCnilSettingFetchSucceeded = createAction(
  '@platform/query/apps/get/singleParam/cnil/success',
);
export const appsCnilSettingFetchFailed = createAction(
  '@platform/query/apps/get/singleParam/cnil/failed',
);

@Service()
export class Apps {
  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('ConfirmationCrate.confirmation') private readonly confirmation: Confirmation,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFail: NotifyOnFail,
    @inject('NotificationCrate.interceptors.NotifyAboutCreation') private readonly notifyAboutCreation: NotifyAboutCreation,
    @inject('NotificationCrate.interceptors.NotifyAboutDelete') private readonly notifyAboutDelete: NotifyAboutDelete,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdate: NotifyAboutUpdate,
    @inject('config') private readonly config: any,
    @inject('productAnalytics') private productAnalytics: ProductAnalytics,
  ) {}

  async find(options?: FindOptions) {
    const finalOptions = merge<
    Required<Pick<FindOptions, 'limit' | 'offset' | 'sort'>>,
    FindOptions
    >({
      sort: {
        by: 'name',
        direction: SortDirection.ASC,
      },
      limit: this.config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE'),
      offset: 0,
    }, options || {});

    this.store.dispatch(appsFetchInitialized(finalOptions));

    try {
      const queryParams = {
        sort: finalOptions.sort.direction + finalOptions.sort.by,
        offset: finalOptions.offset,
        limit: finalOptions.limit,
        search: finalOptions.search || '',
      };
      const resp: CollectionResponse<App> = await this.httpClient.request({
        endpoint: this.config.get('APPS_URL'),
        queryParams,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send();
      this.store.dispatch(appsFetchSucceeded(resp));

      return resp;
    } catch (err) {
      this.store.dispatch(appsFetchFailed());

      throw err;
    }
  }

  async create(attributes: Partial<AppAttributes>, options?: { hideConfirmation?: boolean }) {
    const defaultAttributes = {
      timezone: 'UTC',
      appType: 'web',
      currency: 'USD',
    };

    this.store.dispatch(appCreationInitialized());
    try {
      let request = this.httpClient.request({
        endpoint: this.config.get('APPS_URL'),
        method: 'POST',
        body: {
          data: {
            attributes: {
              ...defaultAttributes,
              ...attributes,
            },
            type: 'ppms/app',
          },
        },
        delay: this.config.get('REQUEST_DELAY'),
      });

      if (!options?.hideConfirmation) {
        request = request.intercept(this.notifyAboutCreation);
      }

      const resp: CreateResponse<App> = await request.jsonApi().send();

      return resp.data;
    } catch (err) {
      this.store.dispatch(appCreationFailed());

      throw err;
    }
  }

  async get(id: AppId) {
    this.store.dispatch(appSettingsFetchInitialized());

    try {
      const resp: DetailsResponse<App> = await this.httpClient.request({
        endpoint: `${this.config.get('APPS_URL')}/${id}`,
        method: 'GET',
      }).intercept(this.notifyOnFail).jsonApi().send();

      this.store.dispatch(appSettingsFetchSucceeded(resp.data));

      return resp.data;
    } catch (err) {
      this.store.dispatch(appSettingsFetchFailed());

      throw err;
    }
  }

  async delete(app: App) {
    await this.confirmation.confirm({
      messages: [
        'administration:apps.deleteConfirm.message-line-1',
        'administration:apps.deleteConfirm.message-line-2',
      ],
      title: 'administration:apps.delete.confirm-title-with-name',
      name: app.attributes.name,
      type: 'withCheck',
      applyBtnAppearance: 'destructive',
      applyBtnText: 'profile:deleteConfirm.modal.submit',
      asyncAction: async () => {
        await this.httpClient.request({
          endpoint: `${this.config.get('APPS_URL')}/${app.id}`,
          method: 'DELETE',
        }).intercept(this.notifyAboutDelete).jsonApi().send();
        this.store.dispatch(appDeleteSucceeded());
      },
    });
  }

  async update(
    id: AppId,
    attributes: Partial<AppAttributes>,
    { withConfirmation } = { withConfirmation: false },
    hideConfirmation?: boolean,
  ) {
    this.store.dispatch(appSettingsUpdateInitialized(attributes));

    const request = async () => {
      try {
        let httpRequest = this.httpClient.request({
          endpoint: `${this.config.get('APPS_URL')}/${id}`,
          method: 'PATCH',
          body: {
            data: {
              type: 'ppms/app',
              id,
              attributes,
            },
          },
        });

        if (!hideConfirmation) {
          httpRequest = httpRequest.intercept(this.notifyAboutUpdate);
        }

        await httpRequest.jsonApi().send();

        if (attributes.realTimeDashboards !== undefined) {
          this.productAnalytics.push({ type: `@platform/command/apps/update/realTimeDashboards/${attributes.realTimeDashboards ? 'turnOn' : 'turnOff'}/success` });
        }

        this.store.dispatch(appSettingsUpdateSucceeded(attributes));
      } catch (err) {
        if (attributes.realTimeDashboards !== undefined) {
          this.productAnalytics.push({ type: `@platform/command/apps/update/realTimeDashboards/${attributes.realTimeDashboards ? 'turnOn' : 'turnOff'}/failed` });
        }

        this.store.dispatch(appSettingsUpdateFailed());

        throw err;
      }
    };

    if (withConfirmation) {
      await this.confirmation.confirm({
        title: 'administration:apps.details.settings.general.currency-with-conversion.change-modal.title',
        messages: [
          'apps.details.settings.general.currency-with-conversion.change-modal.message-1',
          'apps.details.settings.general.currency-with-conversion.change-modal.message-2',
        ],
        applyBtnText: 'administration:apps.details.settings.general.currency-with-conversion.change-modal.confirm',
        applyBtnAppearance: 'primary',
        asyncAction: request,
      });
      return;
    }

    await request();
  }

  async rename(id: AppId, newName: string) {
    return this.update(id, { name: newName });
  }

  async getInfoAboutCnilSetting() {
    this.store.dispatch(appsCnilSettingFetchInitialized());

    try {
      const response: {
        cnil: boolean
      } = await this.httpClient.request({
        endpoint: `${this.config.get('APPS_URL')}/cnil`,
        method: 'GET',
      }).json().send();

      this.store.dispatch(appsCnilSettingFetchSucceeded());

      return response;
    } catch (err) {
      this.store.dispatch(appsCnilSettingFetchFailed());

      throw err;
    }
  }
}
